import { format } from "date-fns";
import { COMPANY, money, qtyFmt, vatSplit } from "../lib/api";

function Totals({ total, thermal }) {
  const [net, vat] = vatSplit(total);
  return (
    <div className="mt-3 border-t-2 border-slate-900 pt-2">
      <div className="flex justify-between text-slate-600"><span>Subtotal (excl. VAT)</span><span className="font-mono" data-testid="doc-subtotal">{money(net)}</span></div>
      <div className="flex justify-between text-slate-600"><span>VAT 18%</span><span className="font-mono" data-testid="doc-vat">{money(vat)}</span></div>
      <div className={`mt-1 flex justify-between border-t border-slate-300 pt-1 font-bold ${thermal ? "" : "text-lg"}`}>
        <span>TOTAL (incl. VAT)</span><span className="font-mono" data-testid="doc-total">{money(total)}</span>
      </div>
    </div>
  );
}

export function DocSheet({ kind, number, meta, items, total, footer, thermal = false, stamp }) {
  const wrap = thermal ? "w-[80mm] p-3 text-[11px] font-mono text-center" : "max-w-[820px] p-6 sm:p-10 text-sm";
  return (
    <div className={`print-sheet rise relative mx-auto overflow-hidden rounded-lg bg-white text-slate-900 shadow-2xl ${wrap}`} data-testid="doc-sheet">
      {stamp && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center" data-testid="doc-cancelled-stamp">
          <span className={`-rotate-12 rounded border-4 border-rose-600/70 px-4 py-1 font-display font-extrabold uppercase tracking-widest text-rose-600/70 ${thermal ? "text-2xl" : "text-6xl"}`}>Cancelled</span>
        </div>
      )}
      <header className={thermal ? "space-y-0.5 border-b border-dashed border-slate-400 pb-2" : "flex flex-col gap-4 border-b-2 border-slate-900 pb-5 sm:flex-row sm:justify-between"}>
        <div>
          <div className={thermal ? "text-sm font-bold" : "font-display text-3xl font-extrabold uppercase tracking-tight"} data-testid="doc-company-name">{COMPANY.name}</div>
          <div className="text-slate-600">{COMPANY.address}</div>
          <div className="text-slate-600">{COMPANY.email}</div>
          <div className="font-medium text-slate-700" data-testid="doc-tax-ids">TIN: {COMPANY.tin} · VRN: {COMPANY.vrn}</div>
        </div>
        <div className={thermal ? "pt-1" : "sm:text-right"}>
          <div className={thermal ? "font-bold" : "font-display text-2xl font-extrabold uppercase text-amber-600"} data-testid="doc-kind">{kind}</div>
          <div className="font-mono font-semibold" data-testid="doc-number">{number}</div>
        </div>
      </header>
      <dl className={thermal ? "space-y-0.5 py-2 text-left" : "grid grid-cols-2 gap-x-8 gap-y-1 py-5"}>
        {meta.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="flex gap-2"><dt className="text-slate-500">{k}:</dt><dd className="font-medium">{v}</dd></div>
        ))}
      </dl>
      {stamp && <p className="mb-3 rounded border border-rose-300 bg-rose-50 px-3 py-2 text-left text-rose-700" data-testid="doc-cancel-note">{stamp}</p>}
      <table className={`w-full ${thermal ? "text-left" : "rpt-table"}`} data-testid="doc-items-table">
        <thead><tr className={thermal ? "border-y border-dashed border-slate-400" : ""}><th className="py-1 text-left">Item</th><th className="text-right">Qty</th><th className="text-right">Price</th><th className="text-right">Total</th></tr></thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.product_id}><td className="py-1">{i.name}</td><td className="text-right">{qtyFmt(i.qty)}</td><td className="text-right">{money(i.unit_price)}</td><td className="text-right">{money(i.total)}</td></tr>
          ))}
        </tbody>
      </table>
      <Totals total={total} thermal={thermal} />
      <div className={`mt-6 space-y-1 text-slate-600 ${thermal ? "" : "text-center"}`}>
        {footer.map((f) => <p key={f}>{f}</p>)}
        <p className="pt-2 text-[10px] text-slate-400">Printed {format(new Date(), "dd MMM yyyy HH:mm")}</p>
      </div>
    </div>
  );
}

export function SizeToggle({ thermal, setThermal }) {
  return (
    <div className="flex rounded-md border border-slate-700 bg-slate-900 p-0.5">
      {[["a4", "A4"], ["80mm", "80mm thermal"]].map(([k, label]) => (
        <button key={k} type="button" onClick={() => setThermal(k === "80mm")} data-testid={`doc-size-${k}-button`}
          className={`h-9 rounded px-3 text-sm font-medium transition-colors ${thermal === (k === "80mm") ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-slate-100"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

export const ThermalPageStyle = () => <style>{"@media print { @page { size: 80mm auto; margin: 3mm; } }"}</style>;
